// @ts-strict-ignore
import { ActionType } from 'flow/store/engine/constants';
import type {
  Action,
  ActionOption,
  Attribute,
  DataRoute,
  Expression,
  Outcome,
  ServiceObject,
  Tag,
  PublishedAttribute,
  ReasonCodeGroup,
  Threshold,
  Rules,
  AttributeComponent,
  List,
} from 'flow/store/engine/types';
import type {
  EquivalencyKey,
  BaseEquivalencyActionObject,
  ActionEquivalencyFnArgs,
  PrimitiveEquivalencyFnArgs,
  WorkflowPrimitive,
  UnpackedDataRoute,
  UnpackedExpression,
} from './types';
import {
  isAction, isOutcome, isTag, isService,
  isPublishedAttribute,
  isReasonCodeGroup,
  isList,
  isServiceAttributePrimitive,
} from './guards';
import { getScrubbedServiceActionOptions } from 'flow/editor/components/SnippetCopyPaste/helpers';
import { isDataRouteExpressionTerm } from 'flow/service/actions/outputAttributes/guards';
import { isDefaultPrecedenceIndexedAction } from 'flow/service/actions';
import { isBooleanRule, isIndexedRule } from 'flow/service/rules';
import { createReasonCodeServiceCodeKey } from 'flow/service/reasonCodes';
import { LIST_SERVICE_KEY_IN_SERVICES } from 'flow/service/services/constants';
import { isListAction } from 'flow/service/actions/services/lists';

export function getEquivalencyKeyForPrimitive(args: PrimitiveEquivalencyFnArgs): EquivalencyKey {
  const { primitive, context } = args;

  // using if-statements instead of switch to leverage type guards and avoid casting the primitive
  if (isAction(primitive)) {
    return getEquivalencyKeyForAction({
      action: primitive,
      context,
    });
  }

  if (isOutcome(primitive)) {
    return getEquivalencyKeyForOutcome(primitive);
  }

  if (isService(primitive)) {
    return getEquivalencyKeyForService(primitive);
  }

  if (isTag(primitive)) {
    return getEquivalencyKeyForTag(primitive);
  }

  if (isPublishedAttribute(primitive)) {
    return getEquivalencyKeyForPublishedAttribute(primitive);
  }

  if (isReasonCodeGroup(primitive)) {
    return getEquivalencyKeyForReasonCodeGroup(primitive);
  }

  if (isList(primitive)) {
    return getEquivalencyKeyForList(primitive);
  }

  if (isServiceAttributePrimitive(primitive)) {
    return getEquivalencyKeyForAttribute(
      primitive.attribute,
      primitive.serviceInternalName,
    );
  }

  throw new Error(`Found unsupported workflow primitive - ${JSON.stringify(primitive)}`);
}


export function getEquivalencyKeyForAction(args: ActionEquivalencyFnArgs): EquivalencyKey {
  // more equivalencyFns will be added as we increase coverage
  switch (args.action.type) {
    case ActionType.INPUT:
      return getEquivalencyKeyForInputAction(args);
    case ActionType.MATRIX_MODEL:
      return getEquivalencyKeyForMatrixModelAction(args);
    case ActionType.OUTCOME:
      return getEquivalencyKeyForOutcomeAction(args);
    case ActionType.OUTPUT:
      return getEquivalencyKeyForOutputAction(args);
    case ActionType.SERVICE: {
      const { action } = args;
      if (isListAction(action)) {
        return getEquivalencyKeyForListAction(args);
      }
      return getEquivalencyKeyForServiceAction(args);
    }
    case ActionType.TAG:
      return getEquivalencyKeyForTagAction(args);
    case ActionType.IMPORTED_MATRIX_MODEL:
      return getEquivalencyKeyForImportedMatrixModelAction(args);
    case ActionType.OUTCOME_REASON:
      return getEquivalencyKeyForImportedOutcomeReasonAction(args);
    case ActionType.IMPORTED_OUTPUT_ATTRIBUTE:
      return getEquivalencyKeyForImportedOutputAttributeAction(args);
    default:
      throw new Error(`Action is not supported - ${JSON.stringify(args.action)}`);
  }
}


function buildBaseActionEquivalencyObject(action: Action): BaseEquivalencyActionObject {
  return {
    type: action.type,
    serviceFailureDefault: action.serviceFailureDefault,
  };
}


function getEquivalencyKeyForInputAction({ action }: ActionEquivalencyFnArgs): EquivalencyKey {
  const { input } = action;

  const inputActionEquivalencyObject = {
    ...buildBaseActionEquivalencyObject(action),
    input: {
      dataType: input.dataType,
      jqFunction: input.jqFunction,
      name: input.name,
      useJq: input.useJq,
    },
  };

  return JSON.stringify(inputActionEquivalencyObject);
}

function getEquivalencyKeyForMatrixModelAction({
  action,
  context,
}: ActionEquivalencyFnArgs): EquivalencyKey {
  const { matrixModel } = action;
  const {
    actionMergeMap,
    workflowData,
  } = context ?? {};


  const dataRouteX = workflowData?.dataRoutes?.[matrixModel.dataRouteX];
  const unpackedDataRouteX: UnpackedDataRoute | null = dataRouteX
    ? unpackDataRoute({ dataRoute: dataRouteX, actionMergeMap })
    : null;

  const dataRouteY = workflowData?.dataRoutes?.[matrixModel.dataRouteY];
  const unpackedDataRouteY: UnpackedDataRoute | null = dataRouteY
    ? unpackDataRoute({ dataRoute: dataRouteY, actionMergeMap })
    : null;

  const matrixModelActionEquivalencyObject = {
    ...buildBaseActionEquivalencyObject(action),
    matrixModel: {
      name: matrixModel.name,
      matrix: matrixModel.matrix,
      matrixDataType: matrixModel.matrixDataType,
      axis: matrixModel.axis,
      dataRouteY: unpackedDataRouteY,
      dataRouteX: unpackedDataRouteX,
    },
  };

  return JSON.stringify(matrixModelActionEquivalencyObject);
}


function getEquivalencyKeyForOutcomeAction({ action }: ActionEquivalencyFnArgs): EquivalencyKey {
  const { outcome } = action;

  const outcomeActionEquivalencyObject = {
    ...buildBaseActionEquivalencyObject(action),
    outcome: {
      name: outcome.name,
      color: outcome.color,
      isDefault: isDefaultPrecedenceIndexedAction(action),
    },
  };

  return JSON.stringify(outcomeActionEquivalencyObject);
}


function getEquivalencyKeyForOutputAction(args: ActionEquivalencyFnArgs): EquivalencyKey {
  const {
    action,
    action: { output },
    context,
  } = args;


  /**
   * Unpacks the expression to use data route references instead of the data route publicId.
   * This allows us to compare the expression of two actions that point to the same action,
   * even if the data route itself is not the same. The unpacked expression includes both
   * the sourceId and attributeId for service data routes to ensure proper equivalency checking.
   */
  let unpackedExpression: Expression | UnpackedExpression = output.expression;
  if (output.expression && context?.workflowData?.dataRoutes) {
    unpackedExpression = unpackDataRoutesInExpression({
      dataRoutes: context.workflowData.dataRoutes,
      expression: output.expression,
      actionMergeMap: context.actionMergeMap,
    });
  }

  const outputActionEquivalencyObject = {
    ...buildBaseActionEquivalencyObject(action),
    output: {
      dataType: output.dataType,
      expression: unpackedExpression ?? null,
      jqFunction: output.jqFunction,
      name: output.name,
      useJq: output.useJq,
    },
  };

  return JSON.stringify(outputActionEquivalencyObject);
}


function getEquivalencyKeyForServiceAction({
  action,
  context,
}: ActionEquivalencyFnArgs): EquivalencyKey {
  const { service, options } = action;
  const { workflowData } = context ?? {};
  const serviceObject = workflowData?.services?.[service.internalName];

  // Filter out auth/secret options and proprietary options
  const scrubbedOptions = getScrubbedServiceActionOptions({
    options,
    service: serviceObject,
  });

  // Extract values and sort by key for consistent JSON output
  const optionsForEquivalency = Object.keys(scrubbedOptions)
    .sort()
    .reduce((acc, key) => {
      const option = scrubbedOptions[key];
      acc[key] = option?.selectedValues ?? option?.value ?? null;
      return acc;
    }, {} as Record<string, ActionOption['value']>);

  const serviceActionEquivalencyObject = {
    ...buildBaseActionEquivalencyObject(action),
    service: {
      internalName: service.internalName,
    },
    options: optionsForEquivalency,
  };

  return JSON.stringify(serviceActionEquivalencyObject);
}

function getEquivalencyKeyForListAction({ action }: ActionEquivalencyFnArgs): EquivalencyKey {
  const { service } = action;

  if (service.isBlacklist) {
    return JSON.stringify({
      ...buildBaseActionEquivalencyObject(action),
      service: {
        internalName: LIST_SERVICE_KEY_IN_SERVICES,
        isBlacklist: service.isBlacklist,
      },
    });
  }

  if (service.isWhitelist) {
    return JSON.stringify({
      ...buildBaseActionEquivalencyObject(action),
      service: {
        internalName: LIST_SERVICE_KEY_IN_SERVICES,
        isWhitelist: service.isWhitelist,
      },
    });
  }

  return JSON.stringify({
    ...buildBaseActionEquivalencyObject(action),
    service: {
      name: service.name,
      internalName: LIST_SERVICE_KEY_IN_SERVICES,
    },
  });
}

function getEquivalencyKeyForTagAction({ action }: ActionEquivalencyFnArgs): EquivalencyKey {
  const { tag } = action;

  const tagActionEquivalencyObject = {
    ...buildBaseActionEquivalencyObject(action),
    tag: {
      name: tag.name,
      color: tag.color,
      description: tag.description || null,
      isAdverseActionReason: tag.isAdverseActionReason,
      isCaseCreating: tag.isCaseCreating,
      suppressionInterval: tag.suppressionInterval,
    },
  };


  return JSON.stringify(tagActionEquivalencyObject);
}

function getEquivalencyKeyForImportedMatrixModelAction({
  action,
}: ActionEquivalencyFnArgs): EquivalencyKey {
  const { importedMatrixModel } = action;

  const importedMatrixModelActionEquivalencyObject = {
    ...buildBaseActionEquivalencyObject(action),
    importedMatrixModel: {
      dataType: importedMatrixModel.dataType,
      displayName: importedMatrixModel.displayName,
    },
  };

  return JSON.stringify(importedMatrixModelActionEquivalencyObject);
}

function getEquivalencyKeyForImportedOutcomeReasonAction({
  action,
}: ActionEquivalencyFnArgs): EquivalencyKey {
  const { outcomeReason } = action;

  const importedOutcomeReasonActionEquivalencyObject = {
    ...buildBaseActionEquivalencyObject(action),
    outcomeReason: {
      dataType: outcomeReason.dataType,
      displayName: outcomeReason.displayName,
    },
  };

  return JSON.stringify(importedOutcomeReasonActionEquivalencyObject);
}

function getEquivalencyKeyForImportedOutputAttributeAction({
  action,
}: ActionEquivalencyFnArgs): EquivalencyKey {
  const { importedOutputAttribute } = action;

  const importedOutputAttributeActionEquivalencyObject = {
    ...buildBaseActionEquivalencyObject(action),
    importedOutputAttribute: {
      dataType: importedOutputAttribute.dataType,
      displayName: importedOutputAttribute.name,
    },
  };


  return JSON.stringify(importedOutputAttributeActionEquivalencyObject);
}


export function getEquivalencyKeyForActionRules(args: {
  thresholds: Record<Threshold['publicId'], Threshold>;
  rules: Rules;
}): EquivalencyKey {
  // recursively build a key for every threshold, preserving the structure of the rules object
  function getRuleEquivalencyKey(rules: Rules) {
    // base case
    if (isIndexedRule(rules)) {
      const threshold = args.thresholds[rules.id];
      return getEquivalencyKeyForThreshold(threshold);
    }

    // compute the equivalency key for any nested indexed rule
    if (isBooleanRule(rules)) {
      const operator = rules.and ? 'and' : 'or';
      const childRuleEquivalencyKeys = rules[operator].map(getRuleEquivalencyKey);
      return {
        [operator]: childRuleEquivalencyKeys,
      };
    }

    /**
     * should not reach this. rules are either Indexed or Boolean
     * the `isBooleanRule` guard is necessary to narrow types better
     */
    return undefined;
  }


  const result = getRuleEquivalencyKey(args.rules);

  return JSON.stringify(result);
}


export function getEquivalencyKeyForThreshold(threshold: Threshold): EquivalencyKey {
  const thresholdEquivalencyObject = {
    actionId: threshold.actionId,
    operator: threshold.operator,
    left: {
      actionId: threshold.left.actionId,
      attributeId: threshold.left.attributeId ?? null,
      attributeComponents: threshold.left.attributeComponents?.length > 0
        ? threshold.left.attributeComponents.map(getEquivalencyKeyForAttributeComponent)
        : null,
      attributeToken: threshold.left.attributeToken ?? null,
    },
    right: {
      actionId: threshold.right?.actionId ?? null,
      attributeComponents: threshold.right?.attributeComponents ?? null,
      attributeId: threshold.right?.attributeId ?? null,
      attributeToken: threshold.right?.attributeToken ?? null,
      reasonCodeGroup: threshold.right?.reasonCodeGroup
        ? getEquivalencyKeyForReasonCodeGroup(threshold.right?.reasonCodeGroup)
        : null,
      reasonCodeGroupId: threshold.right?.reasonCodeGroupId ?? null,
      attributeValue: threshold.right?.attributeValue === 'null'
        ? null
        : threshold.right?.attributeValue?.toString() ?? null,
      leftTagActionResult: threshold.right?.leftTagActionResult ?? null,
    },
  };

  return JSON.stringify(thresholdEquivalencyObject);
}

function getEquivalencyKeyForAttributeComponent(
  component: AttributeComponent,
): EquivalencyKey {
  const attributeComponentEquivalencyObject = {
    value: component.value,
    attributeComponent: {
      dataType: component.attributeComponent.dataType,
      label: component.attributeComponent.label,
      placeholder: component.attributeComponent.placeholder ?? null,
    },
  };
  return JSON.stringify(attributeComponentEquivalencyObject);
}

function getEquivalencyKeyForTag(tag: Tag): EquivalencyKey {
  const tagEquivalencyObject = {
    archived: tag.archived,
    suppression_interval: tag.suppression_interval,
    tag_color: tag.tag_color,
    tag_name: tag.tag_name,
  };

  return JSON.stringify(tagEquivalencyObject);
}

function getEquivalencyKeyForOutcome(outcome: Outcome): EquivalencyKey {
  const outcomeEquivalencyObject = {
    outcome_color: outcome.outcome_color,
    outcome: outcome.outcome,
  };

  return JSON.stringify(outcomeEquivalencyObject);
}

/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
function getEquivalencyKeyForService(service: ServiceObject): EquivalencyKey {
  // todo - implement this once ephemeral artifacts are clearer
  return '';
}

function getEquivalencyKeyForPublishedAttribute(
  publishedAttribute: PublishedAttribute,
): EquivalencyKey {
  const publishedAttributeEquivalencyObject = {
    dataType: publishedAttribute.dataType,
    name: publishedAttribute.attributeName,
  };

  return JSON.stringify(publishedAttributeEquivalencyObject);
}


function getEquivalencyKeyForReasonCodeGroup(
  reasonCodeGroup: ReasonCodeGroup,
): EquivalencyKey {
  const reasonCodeGroupEquivalencyObject = {
    name: reasonCodeGroup.name,
    services: reasonCodeGroup.services ?? [],
    archived: reasonCodeGroup.archived,
    reasonCodes: reasonCodeGroup.reasonCodes
      // numeric and null RCs are invalid
      .filter(rc => typeof rc !== 'number' && rc !== null)
      .map(createReasonCodeServiceCodeKey)
      // sort to guarantee the order in the RC keys
      .sort(),
  };

  return JSON.stringify(reasonCodeGroupEquivalencyObject);
}
/**
 * Lists have a special category (whitelist/blacklist) -
 * there should only be one of each per account, so
 * the equivalency key changes based on those properties
*/
function getEquivalencyKeyForList(list: List): EquivalencyKey {
  if (list.is_whitelist) {
    return JSON.stringify({ isWhitelist: true });
  }

  if (list.is_blacklist) {
    return JSON.stringify({ isBlacklist: true });
  }

  return JSON.stringify({ name: list.list_name });
}

export function getEquivalencyKeyForAttribute(
  attribute: Attribute,
  serviceInternalName: ServiceObject['internal_name'],
): EquivalencyKey {
  const attributeEquivalencyObject = {
    attributeSelector: attribute.selector,
    serviceName: serviceInternalName,
    attributeDisplayName: attribute.display_name,
  };

  return JSON.stringify(attributeEquivalencyObject);
}

/**
 * Takes in an Expression and replaces all data_route_id terms
 * with an UnpackedDataRoute containing both the sourceId and attributeId.
 * This ensures that data routes with the same service action but different
 * attributes are not considered equivalent.
 */
function unpackDataRoutesInExpression(args: {
  expression: Action['output']['expression'];
  dataRoutes: Record<DataRoute['publicId'], DataRoute>;
  actionMergeMap?: Map<Action['publicId'], Action['publicId']>;
}): UnpackedExpression {
  const { dataRoutes, expression, actionMergeMap } = args;


  const unpackedExpression = expression.map(term => {
    if (isDataRouteExpressionTerm(term)) {
      const dataRoute = dataRoutes[term.data_route_id];
      return unpackDataRoute({ dataRoute, actionMergeMap });
    }

    if (Array.isArray(term)) {
      return unpackDataRoutesInExpression({
        expression: term,
        dataRoutes,
        actionMergeMap,
      });
    }

    return term;
  });

  return unpackedExpression;
}


function unpackDataRoute(args: {
  dataRoute: DataRoute;
  actionMergeMap?: Map<Action['publicId'], Action['publicId']>;
}): UnpackedDataRoute {
  const { dataRoute, actionMergeMap } = args;

  const { sourceId } = dataRoute;
  const isSourceIdMerged = actionMergeMap?.has(sourceId);

  return {
    sourceId: isSourceIdMerged ? actionMergeMap.get(sourceId) : sourceId,
    // Explicitly set to null (not undefined) to ensure consistent object shape in JSON
    attributeId: dataRoute.attribute?.id ?? null,
  };
}


/**
 * Returns a record of primitive equivalency keys indexed by the result of
 * the `identifierExtractor` function
 */
export function indexPrimitiveEquivalencyKeysByIdentifier<P extends WorkflowPrimitive>(args: {
  primitives: P[];
  identifierExtractor: (p: P) => string;
}): Record<string, EquivalencyKey> {
  const { primitives, identifierExtractor } = args;

  return primitives.reduce((result, primitive) => {
    const equivalencyKey = getEquivalencyKeyForPrimitive({ primitive });
    const identifier = identifierExtractor(primitive);

    // mutating for better performance
    result[identifier] = equivalencyKey;
    return result;
  }, {});
}
